import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useGlobalQuery } from '@/state/query-client/hooks';
import { useToast } from '@/hooks/use-toast';
import { useErrorHandler } from '@/hooks/use-error-handler';
import graphqlClient from '@/graphql/graphql-client';
import { requestOrganizationLetterheads } from '@/graphql/queries/organization-letterheads';
import { useCurrentOrgId } from '@/store/use-my-oganization-store';
import {
  DELETE_ORGANIZATION_LETTERHEAD_MUTATION,
  INSERT_ORGANIZATION_LETTERHEAD_MUTATION,
  UPDATE_ORGANIZATION_LETTERHEAD_MUTATION,
} from '@/graphql/mutations/organization-letterheads';
import type {
  OrganizationLetterheadInsertInput,
  OrganizationLetterheadUpdateInput,
} from '../types/org-letterheads.types';

const letterheadsKey = (orgId: string | undefined) => ['orgLetterheads', orgId] as const;

const unsetOtherDefaults = async (orgId: string, excludeItemId: string) => {
  const result = await requestOrganizationLetterheads({
    where: { OrganizationId: { eq: orgId } },
    paging: { pageNo: 1, pageSize: 100 },
  });
  const defaults = (result.getOrganizationLetterheadss?.items ?? []).filter(
    (letterhead) => letterhead.IsDefault && letterhead.ItemId !== excludeItemId
  );
  await Promise.all(
    defaults.map((letterhead) =>
      graphqlClient.request(UPDATE_ORGANIZATION_LETTERHEAD_MUTATION, {
        ItemId: letterhead.ItemId,
        IsDefault: false,
      })
    )
  );
};

export const useGetOrgLetterheads = (orgId?: string | undefined) => {
  const currentOrgId = useCurrentOrgId();
  const resolvedOrgId = orgId ?? currentOrgId ?? undefined;
  return useGlobalQuery({
    queryKey: letterheadsKey(resolvedOrgId),
    queryFn: async () => {
      const response = await requestOrganizationLetterheads({
        where: { OrganizationId: { eq: resolvedOrgId } },
        paging: { pageNo: 1, pageSize: 100 },
      });
      return response.getOrganizationLetterheadss ?? { items: [], totalCount: 0 };
    },
    enabled: !!resolvedOrgId,
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
    refetchOnWindowFocus: false,
  });
};

export const useCreateLetterhead = (orgId: string | undefined) => {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const { handleError } = useErrorHandler();
  return useMutation({
    mutationFn: async (input: OrganizationLetterheadInsertInput) => {
      if (input.IsDefault && orgId) {
        await unsetOtherDefaults(orgId, '');
      }
      return graphqlClient.request(INSERT_ORGANIZATION_LETTERHEAD_MUTATION, input);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: letterheadsKey(orgId) });
      queryClient.invalidateQueries({ queryKey: ['organization-letterheads'] });
      toast({ variant: 'success', title: 'Letterhead created successfully' });
    },
    onError: (error: Error) => {
      handleError(error, { defaultMessage: 'Failed to create letterhead' });
    },
  });
};

export const useUpdateLetterhead = (orgId: string | undefined) => {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const { handleError } = useErrorHandler();
  return useMutation({
    mutationFn: async (variables: { itemId: string; input: OrganizationLetterheadUpdateInput }) => {
      if (variables.input.IsDefault && orgId) {
        await unsetOtherDefaults(orgId, variables.itemId);
      }
      return graphqlClient.request(UPDATE_ORGANIZATION_LETTERHEAD_MUTATION, {
        ItemId: variables.itemId,
        ...variables.input,
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: letterheadsKey(orgId) });
      queryClient.invalidateQueries({ queryKey: ['organization-letterheads'] });
      toast({ variant: 'success', title: 'Letterhead updated successfully' });
    },
    onError: (error: Error) => {
      handleError(error, { defaultMessage: 'Failed to update letterhead' });
    },
  });
};

export const useDeleteLetterhead = (orgId: string | undefined) => {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const { handleError } = useErrorHandler();
  return useMutation({
    mutationFn: (itemId: string) =>
      graphqlClient.request(DELETE_ORGANIZATION_LETTERHEAD_MUTATION, {
        ItemId: itemId,
        isHardDelete: true,
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: letterheadsKey(orgId) });
      queryClient.invalidateQueries({ queryKey: ['organization-letterheads'] });
      toast({ variant: 'success', title: 'Letterhead deleted successfully' });
    },
    onError: (error: Error) => {
      handleError(error, { defaultMessage: 'Failed to delete letterhead' });
    },
  });
};
